import { useState, useEffect, useRef, useCallback } from "react";

const useCustomAudioRecorder = (deviceId) => {
  const chunksRef = useRef([]);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);

  const [analyser, setAnalyser] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isStopped, setIsStopped] = useState(null);
  const [dataArray, setDataArray] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioContext, setAudioContext] = useState(null);
  const [audioDuration, setAudioDuration] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const canvasCtx = canvas.getContext("2d");
    let animationFrameId;

    const drawBars = () => {
      if (!analyser || !dataArray) return;

      analyser.getByteFrequencyData(dataArray);

      const barWidth = (canvas.width / dataArray.length) * 1.5;
      let x = 0;
      const centerY = canvas.height / 2;

      canvasCtx.fillStyle = "white";
      canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

      const gradient = canvasCtx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, "#def9fa");
      gradient.addColorStop(0.2, "#bef3f5");
      gradient.addColorStop(0.4, "#9dedf0");
      gradient.addColorStop(0.6, "#7de7eb");
      gradient.addColorStop(0.8, "#5ce1e6");
      gradient.addColorStop(1, "#33bbcf");

      // Use the gradient as the fillStyle for each bar
      canvasCtx.fillStyle = gradient;

      for (let i = 0; i < dataArray.length; i++) {
        const barHeight = (dataArray[i] / 255) * canvas.height;
        const y = centerY - barHeight / 2;

        // Draw each bar with the gradient fill style
        canvasCtx.fillRect(x, y, barWidth, barHeight);
        x += barWidth + 1;
      }

      animationFrameId = requestAnimationFrame(drawBars);
    };

    if (isRecording) {
      drawBars();
    } else {
      canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
    }

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [analyser, dataArray, isRecording]);

  useEffect(() => {
    if (audioBlob) {
      const audioUrl = URL.createObjectURL(audioBlob);
      setAudioUrl(audioUrl);

      const getAudioDuration = async () => {
        try {
          if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext ||
              window.webkitAudioContext)();
          }
          const arrayBuffer = await audioBlob.arrayBuffer();
          const audioBuffer = await audioContextRef.current.decodeAudioData(
            arrayBuffer
          );
          console.log("Audio duration:", audioBuffer.duration);
          setAudioDuration(audioBuffer.duration);
        } catch (error) {
          console.error("Error getting audio duration:", error);
        }
      };

      getAudioDuration();

      return () => {
        URL.revokeObjectURL(audioUrl);
      };
    }
  }, [audioBlob]);

  const startRecording = useCallback(async () => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        const constraints = {
          audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        const recorder = new MediaRecorder(stream);

        const context = new AudioContext();
        setAudioContext(context);
        const source = context.createMediaStreamSource(stream);
        const analyserNode = context.createAnalyser();
        source.connect(analyserNode);
        analyserNode.fftSize = 256;
        const bufferLength = analyserNode.frequencyBinCount;
        const data = new Uint8Array(bufferLength);
        setAnalyser(analyserNode);
        setDataArray(data);

        chunksRef.current = [];

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunksRef.current.push(event.data);
            console.log("Audio data chunk:", event.data);
          }
        };

        recorder.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          setAudioBlob(blob);
        };

        recorder.start(1000);

        setMediaRecorder(recorder);
        setIsRecording(true);
        setIsPaused(false);
      } catch (error) {
        console.error("Error accessing audio device:", error);
      }
    }
  }, [deviceId]);

  const stopRecording = useCallback(() => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach((track) => track.stop());
      setIsRecording(false);
      setIsPaused(false);
      if (audioContext) {
        setIsStopped(true);
        audioContext.close();
      }
    }
  }, [mediaRecorder, audioContext]);

  const pauseRecording = () => {
    if (mediaRecorder && isRecording && !isPaused) {
      mediaRecorder.pause();
      setIsPaused(true);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorder && isRecording && isPaused) {
      mediaRecorder.resume();
      setIsPaused(false);
    }
  };

  const getAudioBlob = () => audioBlob;

  // useEffect(() => {
  //   if (isRecording) {
  //     stopRecording();
  //   }
  //   // You might want to automatically start recording with the new device
  //   // startRecording();
  // }, [deviceId, isRecording, stopRecording]);

  return {
    isPaused,
    analyser,
    audioUrl,
    isStopped,
    canvasRef,
    dataArray,
    isRecording,
    mediaRecorder,
    audioDuration,
    audioBlob: getAudioBlob(),
    setAudioBlob,
    setIsStopped,
    setDataArray,
    stopRecording,
    startRecording,
    pauseRecording,
    resumeRecording,
    setIsRecording
  };
};

export default useCustomAudioRecorder;